import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Maximize2, Play, Pause } from "lucide-react";
import { SHOWREEL_URL, SHOWREEL_POSTER } from "../../data";
import { Reveal } from "./primitives";

export default function Showreel() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <section id="showreel" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 noise-bg opacity-30 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6 relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-primary font-semibold mb-3">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient align-middle mr-2" />
              Showreel — 2026
            </p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl">
              A glimpse of the work we <span className="text-gradient">put our name on</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-muted-foreground max-w-md text-base md:text-lg leading-relaxed">
              Events, films, campaigns and launches — engineered end-to-end by VYFT. Watch with sound on.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-border/15 shadow-2xl shadow-primary/10 bg-black aspect-video"
            data-testid="showreel-section"
          >
            <video
              ref={videoRef}
              src={SHOWREEL_URL}
              poster={SHOWREEL_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              data-testid="showreel-video"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/60 via-transparent to-transparent" />

            {/* Corner label */}
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 border border-white/15">
              <span className="h-2 w-2 rounded-full bg-brand-gradient animate-pulse" /> VYFT Reel
            </div>

            {/* Controls */}
            <div className="absolute bottom-5 right-5 flex items-center gap-2">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? "Pause reel" : "Play reel"}
                data-testid="showreel-play-toggle"
                className="h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 text-white transition"
              >
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "Unmute reel" : "Mute reel"}
                data-testid="showreel-mute-toggle"
                className="h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 text-white transition"
              >
                {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={goFullscreen}
                aria-label="Fullscreen reel"
                data-testid="showreel-fullscreen"
                className="h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 text-white transition"
              >
                <Maximize2 className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
